/**
 * Feb 2026 · Call Status dialog for Mcube outbound calls.
 * Shows agent+customer, live status polling, and — once available —
 * duration, recording player, transcript, and AI summary.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PhoneCall, Loader2, User as UserIcon, ArrowRight, Volume2, FileText, Sparkles, X, AlertTriangle, Target, RefreshCw } from "lucide-react";

const STATUS_META = {
  initiating: { label: "Initiating…", dot: "bg-sky-500 animate-pulse" },
  ringing:    { label: "Ringing",     dot: "bg-sky-500 animate-pulse" },
  connecting: { label: "Connecting",  dot: "bg-amber-500 animate-pulse" },
  connected:  { label: "Connected",   dot: "bg-emerald-500 animate-pulse" },
  in_progress:{ label: "In progress", dot: "bg-emerald-500 animate-pulse" },
  answered:   { label: "Answered",    dot: "bg-emerald-500" },
  completed:  { label: "Completed",   dot: "bg-slate-500" },
  ended:      { label: "Ended",       dot: "bg-slate-500" },
  noanswer:   { label: "No answer",   dot: "bg-amber-500" },
  busy:       { label: "Busy",        dot: "bg-amber-500" },
  failed:     { label: "Failed",      dot: "bg-rose-500" },
  cancelled:  { label: "Cancelled",   dot: "bg-slate-400" },
};

function fmtDuration(sec) {
  const s = Number(sec || 0);
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

export default function CallStatusDialog({ callId, agentNumber, customerNumber,
                                                customerName, agentName,
                                                initialStatus = "initiating",
                                                initialFailure = null,
                                                workspaceId,
                                                onClose }) {
  const [call, setCall] = useState(null);
  const [status, setStatus] = useState(initialStatus);
  const [failure, setFailure] = useState(initialFailure);
  const [briefLang, setBriefLang] = useState("english");
  const [briefBusy, setBriefBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!callId) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/call-logs/${callId}`);
        setCall(data);
        setStatus((data.dialstatus || data.status || status).toLowerCase());
        if (data.failure_reason) setFailure(data.failure_reason);
      } catch { /* ignore polling errors */ }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  const meta = STATUS_META[(status || "").toLowerCase()] || STATUS_META.initiating;
  const isFinal = ["completed", "ended", "noanswer", "busy", "failed", "cancelled"].includes((status || "").toLowerCase());

  const generateBrief = async () => {
    if (!workspaceId || !callId) return;
    setBriefBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/calls/${callId}/generate-brief`,
        { language: briefLang },
      );
      toast.success(
        data.stage === "transcribe+analyse"
          ? "Transcribing recording + generating brief… refreshes in ~30s"
          : "Regenerating AI brief… refreshes shortly",
      );
    } catch (e) { toast.error(apiErrorMessage(e, "Could not generate brief")); }
    finally { setBriefBusy(false); }
  };
  const briefAvailable = !!(call?.recording_url || call?.transcript);
  const hasBrief = !!(call?.ai_summary || call?.ai_next_action || call?.ai_key_objection);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="call-status-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-primary" /> Outbound call
          </DialogTitle>
        </DialogHeader>

        {/* Agent → Customer visualisation */}
        <div className="border border-border p-3 flex items-center gap-3 bg-muted/20" data-testid="call-parties">
          <div className="flex-1 min-w-0 text-center">
            <div className="w-10 h-10 mx-auto mb-1 rounded-full bg-primary/10 flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-primary" />
            </div>
            <div className="text-[10px] label-eyebrow">Agent</div>
            <div className="text-xs font-semibold truncate">{agentName || call?.agent_name || "You"}</div>
            <div className="text-[10px] font-mono text-muted-foreground truncate">{agentNumber || call?.agent_number || "—"}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0 text-center">
            <div className="w-10 h-10 mx-auto mb-1 rounded-full bg-amber-500/10 flex items-center justify-center">
              <PhoneCall className="w-4 h-4 text-amber-600" />
            </div>
            <div className="text-[10px] label-eyebrow">Customer</div>
            <div className="text-xs font-semibold truncate">{customerName || call?.customer_name || "—"}</div>
            <div className="text-[10px] font-mono text-muted-foreground truncate">{customerNumber || call?.customer_number || "—"}</div>
          </div>
        </div>

        {/* Status pill */}
        <div className="flex items-center gap-2" data-testid="call-status-pill">
          <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
          <span className="text-sm font-semibold">{meta.label}</span>
          {call?.duration_seconds != null && call.duration_seconds > 0 && (
            <span className="text-[11px] text-muted-foreground">· {fmtDuration(call.duration_seconds)}</span>
          )}
          {call?.disconnected_by && (
            <span className="text-[10px] text-muted-foreground ml-auto">Hung up by {call.disconnected_by}</span>
          )}
        </div>

        {failure && (
          <div className="border border-rose-400 bg-rose-50/40 p-2 text-[11px] text-rose-800" data-testid="call-status-error">
            {failure}
          </div>
        )}

        {/* Recording */}
        {call?.recording_url && (
          <div className="space-y-1">
            <div className="text-[10px] label-eyebrow inline-flex items-center gap-1">
              <Volume2 className="w-3 h-3" /> Recording
            </div>
            <audio controls src={call.recording_url} className="w-full h-8" data-testid="call-recording" />
          </div>
        )}

        {/* Transcript */}
        {call?.transcript && (
          <div className="space-y-1">
            <div className="text-[10px] label-eyebrow inline-flex items-center gap-1">
              <FileText className="w-3 h-3" /> Transcript
            </div>
            <div className="max-h-40 overflow-y-auto border border-border p-2 text-[11px] whitespace-pre-wrap" data-testid="call-transcript">
              {call.transcript}
            </div>
          </div>
        )}

        {/* AI brief (Feb 2026) — sentiment / next-step / key objection */}
        {(hasBrief || briefAvailable) && (
          <div className="space-y-2" data-testid="call-brief-section">
            <div className="text-[10px] label-eyebrow inline-flex items-center gap-1 text-primary">
              <Sparkles className="w-3 h-3" /> AI post-call brief
              {call?.ai_brief_language && (
                <span className="ml-1 chip text-[9px] chip-internal">
                  {call.ai_brief_language}
                </span>
              )}
            </div>
            {hasBrief && (
              <div className="border border-primary/40 bg-primary/5 p-2 text-[11px] space-y-1.5" data-testid="call-ai-brief">
                {call.ai_summary && (
                  <div data-testid="call-ai-summary">{call.ai_summary}</div>
                )}
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  {call.ai_sentiment && (
                    <span
                      data-testid="call-ai-sentiment"
                      className={`chip text-[10px] ${
                        call.ai_sentiment === "positive" ? "chip-approved"
                        : call.ai_sentiment === "negative" ? "chip-rejected"
                        : "chip-status"}`}
                    >{call.ai_sentiment}</span>
                  )}
                  {call.ai_deal_stage && (
                    <span className="chip text-[10px] chip-internal" data-testid="call-ai-stage">{call.ai_deal_stage}</span>
                  )}
                  {call.ai_followup_urgency && call.ai_followup_urgency !== "none" && (
                    <span
                      className={`chip text-[10px] ${call.ai_followup_urgency === "high" ? "chip-rejected" : "chip-pending"}`}
                      data-testid="call-ai-urgency"
                    >urgency: {call.ai_followup_urgency}</span>
                  )}
                </div>
                {call.ai_next_action && (
                  <div className="flex items-start gap-1.5 pt-1" data-testid="call-ai-next-action">
                    <Target className="w-3 h-3 shrink-0 mt-0.5 text-emerald-600" />
                    <div><span className="font-semibold">Next step:</span> {call.ai_next_action}</div>
                  </div>
                )}
                {call.ai_key_objection && (
                  <div className="flex items-start gap-1.5" data-testid="call-ai-objection">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5 text-amber-600" />
                    <div><span className="font-semibold">Key objection:</span> {call.ai_key_objection}</div>
                  </div>
                )}
                {Array.isArray(call.ai_topics) && call.ai_topics.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1" data-testid="call-ai-topics">
                    {call.ai_topics.slice(0, 8).map((t, i) => (
                      <span key={i} className="chip text-[9px]">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {call?.analysis_error && !hasBrief && (
              <div className="border border-rose-400 bg-rose-50/40 p-2 text-[11px] text-rose-800" data-testid="call-brief-error">
                Brief failed: {call.analysis_error}
              </div>
            )}
            {!hasBrief && !call?.transcript && !call?.recording_url && isFinal && (
              <div className="text-[11px] text-muted-foreground" data-testid="call-brief-unavailable">
                Transcript unavailable — this call has no recording yet. Come back once MCube uploads it.
              </div>
            )}
            {briefAvailable && workspaceId && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Select value={briefLang} onValueChange={setBriefLang}>
                  <SelectTrigger className="h-8 text-[11px] w-32 rounded-none" data-testid="call-brief-lang">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="auto">Auto (transcript)</SelectItem>
                    <SelectItem value="Hindi">Hindi</SelectItem>
                    <SelectItem value="Marathi">Marathi</SelectItem>
                    <SelectItem value="Gujarati">Gujarati</SelectItem>
                    <SelectItem value="Tamil">Tamil</SelectItem>
                    <SelectItem value="Telugu">Telugu</SelectItem>
                    <SelectItem value="Kannada">Kannada</SelectItem>
                    <SelectItem value="Bengali">Bengali</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={generateBrief}
                  disabled={briefBusy}
                  className="rounded-none h-8 text-[11px]"
                  data-testid="call-generate-brief"
                >
                  {briefBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> :
                    hasBrief ? <RefreshCw className="w-3.5 h-3.5 mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  {hasBrief ? "Regenerate brief" : "Generate brief"}
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {isFinal ? (
            <Button onClick={onClose} className="rounded-none" data-testid="call-status-close">
              <X className="w-3.5 h-3.5 mr-1" /> Close
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="call-status-hide">
              Keep in background
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
